import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, Typography, Select, MenuItem, FormControl, InputLabel, Collapse, Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import dayjs from 'dayjs';
import api from "../../../api/api";
import Loading from '../../../components/loading';
import { useParams } from 'react-router-dom';

const attestedTypes = [{
    value: 'COMPARECIMENTO',
    label: 'Atestado de Comparecimento na Consulta'
},
{
    value: 'REPOUSO',
    label: 'Atestado de Repouso'
}
];

export default function AttestedModal({ open, onClose, onAttestedSave }) {

    const { patientId } = useParams();

    const [isLoading, setLoading] = useState(false);

    const [comboBoxDentists, setComboBoxDentists] = useState([]);
    const [selectedDentistId, setSelectedDentistId] = useState('');

    const [selectedAttestedType, setSelectedAttestedType] = useState('');
    const [date, setDate] = useState(dayjs());
    const [daysOfRest, setDaysOfRest] = useState('');
    const [cid, setCid] = useState('');
    const [error, setError] = useState('');
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);


    const getComboBoxDentists = useCallback(() => {
        api.get("/dentists/combobox", {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setComboBoxDentists(returnFromApi.data);
        }).catch((err) => {
            console.error("ops! ocorreu um erro" + err);
        });
    }, []);

    useEffect(() => {
        const dentistIdFromUser = sessionStorage.getItem('dentistIdFromUser');
        if (dentistIdFromUser && dentistIdFromUser !== 'null' && dentistIdFromUser !== 'undefined') {
            setSelectedDentistId(dentistIdFromUser);
        }

        getComboBoxDentists();
    }, [getComboBoxDentists]);

    const saveAttested = () => {
        setError('');
        if (!selectedDentistId || !selectedAttestedType || !date || !cid) {
            setError('Todos os campos são obrigatórios.');
            return;
        }
        if (selectedAttestedType === 'REPOUSO' && (!daysOfRest || parseInt(daysOfRest) <= 0 || !selectedDentistId || !cid)) {
            setError('Todos os campos são obrigatórios.');
            return;
        }

        const attested = {
            dentistaId: selectedDentistId,
            pacienteId: patientId,
            tipoDeAtestado: selectedAttestedType,
            cid: cid,
            quantidadeDeDiasDeRepouso: parseInt(daysOfRest) || 0,
            horaInicial: dayjs(startTime).format("HH:mm"),
            horaFinal: dayjs(endTime).format("HH:mm"),
            data: date
        };

        setLoading(true);
        api.post("/atestado", attested, {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            onAttestedSave(returnFromApi);
            cleanFields();
            setLoading(false);
        }).catch((returnFromApi) => {
            onAttestedSave(returnFromApi);
            console.error("Erro ao salvar atestado:", returnFromApi);
            setLoading(false);
        });
    };

    function cleanFields() {
        setSelectedAttestedType('');
        setDate(dayjs());
        setDaysOfRest('');
        setCid('');
        setStartTime('');
        setEndTime('');
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">

            <Loading isToHidden={isLoading === false} />

            <DialogTitle>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Atestado
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Collapse in={!!error} sx={{ mt: 1, mb: 1 }}>
                    <Alert severity="error" onClose={() => setError('')}>
                        {error}
                    </Alert>
                </Collapse>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={12}>
                        <FormControl fullWidth>
                            <InputLabel id="dentist-select-label">Dentista</InputLabel>
                            <Select fullWidth
                                labelId="dentist-combobox-label"
                                id="dentist-combobox"
                                value={selectedDentistId}
                                label="Dentista"
                                onChange={(e) => setSelectedDentistId(e.target.value)}>

                                {comboBoxDentists.map((dentist) => (
                                    <MenuItem value={dentist.id}>{dentist.nome}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={12}>
                        <FormControl fullWidth>
                            <InputLabel id="cert-type-label">Tipo de Atestado</InputLabel>
                            <Select
                                value={selectedAttestedType}
                                label="Tipo de Atestado"
                                onChange={(e) => setSelectedAttestedType(e.target.value)}
                            >
                                {attestedTypes.map(type => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={6}>
                        <DatePicker
                            label="Data"
                            value={date}
                            onChange={(newValue) => setDate(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </Grid>
                    {selectedAttestedType === 'REPOUSO' && (
                        <>
                            <Grid size={6}>
                                <TextField
                                    fullWidth
                                    label="Dias de repouso"
                                    type="number"
                                    value={daysOfRest}
                                    onChange={(e) => setDaysOfRest(e.target.value)}
                                />
                            </Grid>
                        </>
                    )}
                    {selectedAttestedType === 'COMPARECIMENTO' && (
                        <>
                            <Grid size={3}>
                                <TimeField
                                    label="Hora Inicial"
                                    value={startTime}
                                    onChange={(newValue) => setStartTime(newValue)}
                                    format="HH:mm"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid size={3}>
                                <TimeField
                                    label="Hora Final"
                                    value={endTime}
                                    onChange={(newValue) => setEndTime(newValue)}
                                    format="HH:mm"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid size={12}>
                        <TextField
                            fullWidth
                            label="CID"
                            value={cid}
                            onChange={(e) => setCid(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={saveAttested} variant="contained">Salvar</Button>
            </DialogActions>
        </Dialog>
    );
}